/**
 * OnboardingFlow Component
 * Onboarding guiado para novos usuarios da plataforma
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useLayoutEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowRight,
  IconArrowLeft,
  IconX,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import UserContext from "../../contexts/UserContext";
import OnboardingService from "./services/OnboardingServices";
import OnboardingProgress from "./components/OnboardingProgress";
import WelcomeStep from "./components/WelcomeStep";
import ApiKeyStep from "./components/ApiKeyStep";
import NavigationStep from "./components/NavigationStep";
import LearningStep from "./components/LearningStep";
import AgentsStep from "./components/AgentsStep";
import BillingStep from "./components/BillingStep";
import SettingsStep from "./components/SettingsStep";
import CompletionStep from "./components/CompletionStep";
import "./css/Onboarding.css";

// ===============================================
// STEPS CONFIGURATION
// ===============================================

const STEPS = [
  {
    id: "welcome",
    component: WelcomeStep,
    position: "center",
    targetSelector: null,
  },
  {
    id: "apikey",
    component: ApiKeyStep,
    position: "center",
    targetSelector: null,
  },
  {
    id: "navigation",
    component: NavigationStep,
    position: "right",
    targetSelector: ".sidebar-menu",
  },
  {
    id: "learning",
    component: LearningStep,
    position: "right",
    targetSelector: '[data-menu-id="aprendizado"]',
  },
  {
    id: "agents",
    component: AgentsStep,
    position: "right",
    targetSelector: '[data-menu-id="agentes"]',
  },
  {
    id: "billing",
    component: BillingStep,
    position: "right",
    targetSelector: '[data-menu-id="financeiro"]',
  },
  {
    id: "settings",
    component: SettingsStep,
    position: "right",
    targetSelector: '[data-menu-id="configuracoes"]',
  },
  {
    id: "completion",
    component: CompletionStep,
    position: "center",
    targetSelector: null,
  },
];

const TOTAL_STEPS = STEPS.length;
const SPOTLIGHT_PADDING = 8;
const CARD_WIDTH = 420;
const CARD_GAP = 20;
const VIEWPORT_MARGIN = 16;

// ===============================================
// HELPER: computa posicao do card e spotlight
// ===============================================

const computePositions = (targetSelector) => {
  if (!targetSelector) return { spotlight: null, card: null };

  const target = document.querySelector(targetSelector);
  if (!target) return { spotlight: null, card: null };

  // Se o alvo esta dentro de um container scrollavel, scroll ate ele
  const scrollParent = target.closest(".sidebar");
  if (scrollParent) {
    const parentRect = scrollParent.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    // Verifica se o elemento esta fora da area visivel do sidebar
    const isAboveView = targetRect.top < parentRect.top;
    const isBelowView = targetRect.bottom > parentRect.bottom;

    if (isAboveView || isBelowView) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      // Retorna null para recalcular apos o scroll
      return { spotlight: null, card: null, needsRecalc: true };
    }
  }

  const rect = target.getBoundingClientRect();

  const spotlight = {
    top: rect.top - SPOTLIGHT_PADDING,
    left: rect.left - SPOTLIGHT_PADDING,
    width: rect.width + SPOTLIGHT_PADDING * 2,
    height: rect.height + SPOTLIGHT_PADDING * 2,
  };

  // Posicionar card a direita do alvo
  const cardLeft = rect.right + CARD_GAP;
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  let card;

  // Card sempre comeca no topo para maximizar espaco vertical
  const top = VIEWPORT_MARGIN;
  const maxHeight = viewportHeight - VIEWPORT_MARGIN * 2;

  if (cardLeft + CARD_WIDTH > viewportWidth - VIEWPORT_MARGIN) {
    // Nao cabe a direita — centralizar horizontalmente
    card = {
      top,
      left: Math.max(VIEWPORT_MARGIN, (viewportWidth - CARD_WIDTH) / 2),
      maxHeight,
    };
  } else {
    card = {
      top,
      left: cardLeft,
      maxHeight,
    };
  }

  return { spotlight, card };
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const OnboardingFlow = ({ onComplete }) => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [cardPosition, setCardPosition] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPositionReady, setIsPositionReady] = useState(true);

  const cardRef = useRef(null);
  const recalcTimerRef = useRef(null);

  // Analytics tracking
  const analyticsRef = useRef({
    startedAt: new Date().toISOString(),
    stepTimes: {},
    stepEnteredAt: null,
    skippedAtStep: null,
    completedAllSteps: false,
  });

  // Track step entry time
  const trackStepEntry = useCallback(() => {
    analyticsRef.current.stepEnteredAt = Date.now();
  }, []);

  // Track step exit and duration
  const trackStepExit = useCallback((stepId) => {
    if (analyticsRef.current.stepEnteredAt) {
      const duration = Date.now() - analyticsRef.current.stepEnteredAt;
      analyticsRef.current.stepTimes[stepId] = duration;
    }
  }, []);

  // Send analytics event
  const sendAnalytics = useCallback(
    async (event, data = {}) => {
      try {
        await OnboardingService.trackAnalytics({
          event,
          step: STEPS[currentStep]?.id,
          step_number: currentStep + 1,
          total_steps: TOTAL_STEPS,
          timestamp: new Date().toISOString(),
          ...data,
        });
      } catch {
        // Analytics silencioso
      }
    },
    [currentStep]
  );

  // Fade in on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    trackStepEntry();
    sendAnalytics("onboarding_started");
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===============================================
  // POSITION CALCULATION
  // ===============================================

  const updatePositions = useCallback(() => {
    const step = STEPS[currentStep];

    if (step.position === "center" || !step.targetSelector) {
      setSpotlightRect(null);
      setCardPosition(null);
      setIsPositionReady(true);
      return;
    }

    const result = computePositions(step.targetSelector);

    if (result.needsRecalc) {
      // O elemento precisou de scroll — recalcular apos animacao de scroll
      recalcTimerRef.current = setTimeout(() => {
        const recalc = computePositions(step.targetSelector);
        setSpotlightRect(recalc.spotlight);
        setCardPosition(recalc.card);
        setIsPositionReady(true);
      }, 400);
      return;
    }

    setSpotlightRect(result.spotlight);
    setCardPosition(result.card);
    setIsPositionReady(true);
  }, [currentStep]);

  // Recalculate on step change
  useEffect(() => {
    const step = STEPS[currentStep];
    trackStepEntry();

    // Limpar timer anterior
    if (recalcTimerRef.current) {
      clearTimeout(recalcTimerRef.current);
    }

    if (step.position === "center") {
      setSpotlightRect(null);
      setCardPosition(null);
      setIsPositionReady(true);
      return;
    }

    // Esconder card ate posicao estar pronta (evita flash)
    setIsPositionReady(false);

    // Pequeno delay para DOM estar pronto, entao calcula posicao
    const timer = setTimeout(updatePositions, 50);

    return () => {
      clearTimeout(timer);
      if (recalcTimerRef.current) {
        clearTimeout(recalcTimerRef.current);
      }
    };
  }, [currentStep, updatePositions, trackStepEntry]);

  // Ajustar card vertical se exceder viewport (apos render)
  // Usa ref para evitar loop: so ajusta uma vez por mudanca de step
  const lastAdjustedStepRef = useRef(-1);

  useLayoutEffect(() => {
    if (!cardRef.current || !cardPosition || !isPositionReady) return;
    // Evitar ajuste duplicado para o mesmo step
    if (lastAdjustedStepRef.current === currentStep) return;

    const cardEl = cardRef.current;
    const cardRect = cardEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Se o bottom do card ultrapassa o viewport
    if (cardRect.bottom > viewportHeight - VIEWPORT_MARGIN) {
      const newTop = Math.max(
        VIEWPORT_MARGIN,
        viewportHeight - cardRect.height - VIEWPORT_MARGIN
      );
      const newMaxHeight = viewportHeight - newTop - VIEWPORT_MARGIN;
      lastAdjustedStepRef.current = currentStep;
      setCardPosition((prev) =>
        prev ? { ...prev, top: newTop, maxHeight: newMaxHeight } : prev
      );
    }
  });

  // Resize handler
  useEffect(() => {
    const handleResize = () => updatePositions();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updatePositions]);

  // ===============================================
  // NAVIGATION HANDLERS
  // ===============================================

  const handleNext = useCallback(() => {
    const stepId = STEPS[currentStep]?.id;
    trackStepExit(stepId);
    sendAnalytics("step_completed", { step_id: stepId });

    if (currentStep < TOTAL_STEPS - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, trackStepExit, sendAnalytics]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      trackStepExit(STEPS[currentStep]?.id);
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep, trackStepExit]);

  const handleComplete = useCallback(
    async (navigateTo = null) => {
      trackStepExit(STEPS[currentStep]?.id);
      analyticsRef.current.completedAllSteps = true;

      const analyticsData = {
        started_at: analyticsRef.current.startedAt,
        completed_at: new Date().toISOString(),
        step_times: analyticsRef.current.stepTimes,
        total_steps_viewed: currentStep + 1,
        completed: true,
      };

      try {
        await OnboardingService.complete(analyticsData);
      } catch (error) {
        console.warn("Error saving onboarding completion:", error);
      }

      sendAnalytics("onboarding_completed", analyticsData);

      setIsVisible(false);
      setTimeout(() => {
        onComplete();
        if (navigateTo) {
          navigate(navigateTo);
        }
      }, 300);
    },
    [currentStep, onComplete, navigate, trackStepExit, sendAnalytics]
  );

  const handleSkip = useCallback(async () => {
    const result = await Swal.fire({
      title: "Pular o guia?",
      text: "Recomendamos fortemente que você assista os Vídeos Tutoriais antes de usar a plataforma.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Sim, pular",
      cancelButtonText: "Continuar o guia",
      customClass: { container: "onb-swal" },
    });

    if (result.isConfirmed) {
      analyticsRef.current.skippedAtStep = STEPS[currentStep]?.id;

      const skipData = {
        skipped_at_step: currentStep + 1,
        skipped_step_id: STEPS[currentStep]?.id,
        started_at: analyticsRef.current.startedAt,
        skipped_at: new Date().toISOString(),
        step_times: analyticsRef.current.stepTimes,
      };

      try {
        await OnboardingService.skip(skipData);
      } catch (error) {
        console.warn("Error saving onboarding skip:", error);
      }

      sendAnalytics("onboarding_skipped", skipData);

      setIsVisible(false);
      setTimeout(() => onComplete(), 300);
    }
  }, [currentStep, onComplete, sendAnalytics]);

  // Keyboard navigation — refs para evitar stale closures
  const handleNextRef = useRef(handleNext);
  const handlePrevRef = useRef(handlePrev);
  const handleSkipRef = useRef(handleSkip);
  const currentStepRef = useRef(currentStep);

  useEffect(() => {
    handleNextRef.current = handleNext;
    handlePrevRef.current = handlePrev;
    handleSkipRef.current = handleSkip;
    currentStepRef.current = currentStep;
  }, [handleNext, handlePrev, handleSkip, currentStep]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        // No ultimo step, Enter nao faz nada (usuario deve escolher um CTA)
        if (
          e.key === "Enter" &&
          currentStepRef.current === TOTAL_STEPS - 1
        ) {
          return;
        }
        handleNextRef.current();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrevRef.current();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleSkipRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ===============================================
  // RENDER
  // ===============================================

  const step = STEPS[currentStep];
  const StepComponent = step.component;
  const isCentered = step.position === "center";
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOTAL_STEPS - 1;
  const hasSpotlight = spotlightRect && !isCentered;

  return (
    <div className="onb-overlay">
      {/* Backdrop escuro — SOMENTE quando NAO ha spotlight
          Quando ha spotlight, o box-shadow do spotlight ja cria o overlay escuro */}
      {!hasSpotlight && (
        <div
          className={`onb-overlay__backdrop ${
            isVisible
              ? "onb-overlay__backdrop--visible"
              : "onb-overlay__backdrop--entering"
          }`}
        />
      )}

      {/* Spotlight no elemento alvo */}
      {hasSpotlight && (
        <div
          className="onb-spotlight"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
          }}
        />
      )}

      {/* Card do step */}
      <div
        ref={cardRef}
        className={`onb-card ${isCentered ? "onb-card--center" : ""} ${
          !isCentered && !isPositionReady ? "onb-card--hidden" : ""
        }`}
        style={
          isCentered
            ? {}
            : cardPosition
            ? {
                top: `${cardPosition.top}px`,
                left: `${cardPosition.left}px`,
                maxHeight: cardPosition.maxHeight
                  ? `${cardPosition.maxHeight}px`
                  : undefined,
              }
            : { visibility: "hidden" }
        }
      >
        {/* Botao fechar (pular) — nao mostrar no ultimo step */}
        {!isLastStep && (
          <button
            className="onb-btn onb-btn--ghost onb-card__close"
            onClick={handleSkip}
            title="Pular onboarding"
          >
            <IconX size={16} />
          </button>
        )}

        {/* Conteudo do step com scroll se necessario */}
        <div className="onb-card__scrollable">
          <StepComponent userName={user?.first_name} />
        </div>

        {/* Progress bar */}
        <OnboardingProgress
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
        />

        {/* Footer com botoes */}
        <div className="onb-card__footer">
          {isFirstStep ? (
            <>
              <button
                className="onb-btn onb-btn--ghost"
                onClick={handleSkip}
              >
                Pular
              </button>
              <button
                className="onb-btn onb-btn--primary"
                onClick={handleNext}
              >
                Vamos lá
                <IconArrowRight size={16} />
              </button>
            </>
          ) : isLastStep ? (
            <>
              <button
                className="onb-btn onb-btn--secondary"
                onClick={() => handleComplete()}
              >
                Explorar a plataforma
              </button>
              <button
                className="onb-btn onb-btn--success"
                onClick={() => handleComplete("/courses")}
              >
                Ir para Vídeos Tutoriais
                <IconArrowRight size={16} />
              </button>
            </>
          ) : (
            <>
              <button
                className="onb-btn onb-btn--secondary"
                onClick={handlePrev}
              >
                <IconArrowLeft size={16} />
                Voltar
              </button>
              <button
                className="onb-btn onb-btn--primary"
                onClick={handleNext}
              >
                Entendi
                <IconArrowRight size={16} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Keyboard hints */}
      {!isCentered && isPositionReady && (
        <div
          className="onb-kbd-hint"
          style={{
            position: "fixed",
            bottom: "1rem",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <span className="onb-kbd-hint__key">&larr;</span>
          <span className="onb-kbd-hint__key">&rarr;</span>
          <span>para navegar</span>
          <span className="onb-kbd-hint__key">Esc</span>
          <span>para pular</span>
        </div>
      )}
    </div>
  );
};

export default OnboardingFlow;
